import { postToBackend } from "./portalClient";

/**
 * Partner wallet top-up via Razorpay (SMB-3932) — INR only.
 *
 *   POST /app/v1/partner-wallet/recharge/orders   create the Razorpay order
 *   POST /app/v1/partner-wallet/recharge/verify   hand back Checkout's signature
 *   POST /app/v1/partner-wallet/recharge/status   what the attempt is now
 *   POST /app/v1/partner-wallet/balance           the wallet itself
 *
 * ── ⭐ VERIFY DOES NOT MEAN THE MONEY ARRIVED ──────────────────────────────
 * `verifyRecharge` resolving is NOT a completed top-up. It reports that the
 * Checkout signature checked out, and leaves the attempt at PENDING. The wallet
 * is credited when Razorpay's webhook reaches the booking engine directly — a
 * call that never passes through this app or app-backend.
 *
 * So the only thing that may drive a success message is `status: 'SUCCESS'`
 * from `getRechargeStatus`. Anything that announces a top-up off the verify
 * response tells partners their money landed before it has.
 *
 * It also means verify is OPTIONAL. A partner paying by UPI approves in their
 * banking app and often never returns to the tab, so no Checkout handler ever
 * fires — and the webhook credits them anyway. Poll for the truth; treat the
 * Checkout callback as an accelerator.
 *
 * ── The envelope is SINGLE here, unlike financeAPI ─────────────────────────
 * app-backend's axios layer already strips the booking engine's
 * `{statusCode, data}` wrapper (`result.data.data` in axios_services.ts) before
 * SuccessResponse wraps it once. portalClient strips that one, so `result.data`
 * is the recharge object itself. Do NOT copy financeAPI's `unwrapInner` — the
 * finance service wraps a second time and the booking engine does not, and
 * unwrapping twice here yields undefined.
 *
 * ── Money is whole rupees on the way in ────────────────────────────────────
 * `amount` is an integer number of rupees. Not paise, not a decimal. The
 * response carries both `amount` (rupees, to display) and `razorpayAmount`
 * (paise, which is what Checkout's own `amount` option wants) so nothing here
 * ever multiplies money: `19.99 * 100` is `1998.9999999999998` in JavaScript.
 *
 * ── The partner is never named by us ───────────────────────────────────────
 * No call in this file sends a partnerCode. app-backend injects it from the
 * resolved session and overwrites anything the body claimed, which is what
 * stops a top-up being aimed at somebody else's wallet. Do not add one.
 */

/** Whole rupees. Matches the booking engine's own bounds — see helper.js. */
export const MIN_TOPUP = 1;

/**
 * ₹1,00,000 — the usual NPCI/bank per-transaction cap for UPI, not an arbitrary
 * ceiling. Above it the payment fails at the bank rather than here, and the
 * partner sees an opaque gateway error instead of a sentence naming the limit.
 */
export const MAX_TOPUP = 100000;

/** The states a recharge can be in, as the engine reports them. */
export const RECHARGE_STATUS = {
  CREATED: "CREATED",
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
  EXPIRED: "EXPIRED",
};

/** Nothing more will happen to an attempt in one of these. Stop polling. */
export const TERMINAL_STATUSES = [
  RECHARGE_STATUS.SUCCESS,
  RECHARGE_STATUS.FAILED,
  RECHARGE_STATUS.EXPIRED,
];

export const isTerminal = (status) => TERMINAL_STATUSES.includes(status);

/**
 * Create the Razorpay order Checkout is opened against.
 *
 * Replaying the same amount inside the engine's dedupe window returns the SAME
 * order with `reused: true`. That is a success, not a conflict — it exists so a
 * double-click cannot produce two live orders and two ways to pay. Treat a
 * reused order exactly like a fresh one.
 *
 * `branchCode` identifies which branch's wallet to credit — read off the
 * session by the caller (`actingBranchCode ?? user.branchCode`), `0` when the
 * session has no branch to pin to. Uniform contract with the Revenue writes.
 *
 * @param {number} amount whole rupees
 * @param {number} branchCode selected branch id, `0` when none
 */
export const createRechargeOrder = (amount, branchCode = 0) =>
  postToBackend(
    "/app/v1/partner-wallet/recharge/orders",
    { amount, branchCode },
    "partner",
  );

/**
 * Hand back what Razorpay Checkout gave the browser.
 *
 * ⚠️ Resolving means the signature verified. It does NOT mean the wallet moved
 * — see the header of this file. Safe to call more than once: if the webhook
 * already settled the attempt, the settled state comes back rather than a
 * second credit.
 */
export const verifyRecharge = ({ rechargeId, razorpayOrderId, razorpayPaymentId, razorpaySignature }) =>
  postToBackend(
    "/app/v1/partner-wallet/recharge/verify",
    { rechargeId, razorpayOrderId, razorpayPaymentId, razorpaySignature },
    "partner",
  );

/**
 * The authoritative state of one attempt. Answers from the engine's own
 * database rather than Razorpay, so it is cheap enough to poll.
 *
 * A rechargeId belonging to another partner answers the same "not found" as one
 * that does not exist — deliberately, so the response cannot confirm that
 * somebody else's attempt is real.
 */
export const getRechargeStatus = (rechargeId) =>
  postToBackend("/app/v1/partner-wallet/recharge/status", { rechargeId }, "partner");

/**
 * The partner's wallets.
 *
 * ⚠️ TWO SHAPES. With `currencyCode` you get ONE wallet object; without it, a
 * `wallets` ARRAY. The list form also omits several columns and returns them as
 * null — `lifetime.credited`, `lifetime.debited`, `lastTransactionAt` among
 * them — so `lifetime.credited: null` there reads as "never credited" when it
 * only means "not asked for". Pass a currency for anything that shows history.
 */
export const getWalletBalance = (currencyCode) =>
  postToBackend(
    "/app/v1/partner-wallet/balance",
    currencyCode ? { currencyCode } : {},
    "partner",
  );
